/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import { padding } from 'polished';
import colorUtils from 'color';
import { filter, identity, map } from 'lodash/fp';
import { inviteOthers } from 'app/actions/userActions';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import InputField from 'app/components/commons/InputField';
import Loader from 'app/components/commons/Loader';
import { hover } from 'app/utils/cssUtils';

const filterTruthy = filter(identity);
const mapRecipients = map(address => ({
  email: address,
  name: 'email-invite-form',
}));

type Props = {
  errors: Array<*>,
  isSubmitting: boolean,
  onSubmit: (Array<string>) => void,
};

type State = {
  address: ?string,
  emailSentTo: ?string,
};

const SuccessMessage = glamorous.div(({ theme }) => ({
  color: theme.color.success,
  fontSize: theme.fontSize.small,
}));

const InputWrapper = glamorous.div({
  flexGrow: 1,
  maxWidth: 400,
});

const InviteButton = glamorous.button(
  {
    borderRadius: '0 10px 10px 0',
    width: 60,
    transition: 'background-color 0.2s',
    outline: 'none',
  },
  ({ theme, disabled }) => ({
    backgroundColor: disabled ? theme.color.basis4 : theme.color.primary,
    color: theme.color.white,
    fontSize: theme.fontSize.default,
    '&:focus > span': {
      borderBottom: `2px solid ${theme.color.white}`,
    },
    ...hover({
      backgroundColor: disabled
        ? theme.color.basis4
        : colorUtils(theme.color.primary)
          .lighten(0.1)
          .string(),
    }),
  }),
);

const ButtonWrapper = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'stretch',
  width: '100%',
});

const Wrapper = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  ...padding(0, 20),
});

class InviteFriendForm extends Component<Props, State> {
  props: Props;
  state: State = {
    address: null,
    emailSentTo: null,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isSubmitting } = this.props;

    if (isSubmitting && !nextProps.isSubmitting && nextProps.errors.length === 0) {
      this.setState(state => ({
        address: null,
        emailSentTo: state.address,
      }));
    }
  }

  handleInput = (value: string) => {
    this.setState({
      address: value,
      emailSentTo: null,
    });
  };

  handleSubmit = (e) => {
    const { address } = this.state;
    e.preventDefault();

    if (window && window.dataLayer) {
      window.dataLayer.push({
        event: 'invitedFriends',
      });
    }

    this.props.onSubmit([address]);
  };

  render() {
    const { errors, isSubmitting } = this.props;
    const { address, emailSentTo } = this.state;

    return (
      <form>
        <Wrapper>
          {errors.length > 0 && <FormValidationMessage message={errors} />}
          <ButtonWrapper>
            <InputWrapper>
              <InputField
                autoBorderRadius={false}
                noMargin
                type="email"
                placeholder="Enter friends email address"
                value={address || ''}
                size="small"
                onChange={this.handleInput}
                lightContent
                name="email"
                inputStyle={{
                  borderRadius: '10px 0 0 10px',
                  outline: 'none',
                }}
              />
            </InputWrapper>
            <InviteButton onClick={this.handleSubmit} disabled={isSubmitting || !address}>
              {isSubmitting ? <Loader /> : <span className="ion-paper-airplane" />}
            </InviteButton>
          </ButtonWrapper>
          {emailSentTo !== null && <SuccessMessage>Email sent to {emailSentTo}</SuccessMessage>}
        </Wrapper>
      </form>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  return {
    errors: state.invite.errors,
    isSubmitting: state.invite.isSubmitting,
  };
}

const actions = {
  onSubmit(addresses) {
    return inviteOthers({
      recipients: mapRecipients(filterTruthy(addresses)),
      message: '',
    });
  },
};

export default connect(mapStateToProps, actions)(InviteFriendForm);
